import React, {useState, useEffect, useCallback, useRef} from "react"
import {Box, Skeleton} from "@mui/material"

import {SMSDraftReplyPanel} from "./SMSDraftReplyPanel"
import {SMSThread} from "./SMSThread"

import {api} from "@indebted/api"
import type {ConversationMessage, ConversationTimeline} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {colors} from "@indebted/theme/colors"

const PAGE_SIZE = 5

function SMSConversationView({customerID, conversationID}: {customerID: string; conversationID: string}) {
	const {notification} = useNotification()
	const [timeline, setTimeline] = useState<ConversationTimeline | null>(null)
	const [messages, setMessages] = useState<ConversationMessage[]>([])
	const [hasMore, setHasMore] = useState(false)
	const [loading, setLoading] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [selectedReplyMessageId, setSelectedReplyMessageId] = useState<string | null>(null)

	const mounted = useRef(true)
	useEffect(() => {
		return () => {
			mounted.current = false
		}
	}, [])

	const loadConversation = useCallback(async () => {
		try {
			const conversation = await api.customer.getConversation(customerID, conversationID, {
				limit: PAGE_SIZE,
				offset: 0,
			})
			if (!mounted.current) return
			setTimeline(conversation)
			setMessages(conversation.messages)
			setHasMore(conversation.hasMore)
		} catch {
			if (mounted.current) notification.error("Failed to load the conversation")
		}
	}, [customerID, conversationID, notification])

	useEffect(() => {
		setLoading(true)
		loadConversation().finally(() => {
			if (mounted.current) setLoading(false)
		})
	}, [loadConversation])

	function showEarlier() {
		setLoadingMore(true)
		api.customer
			.getConversation(customerID, conversationID, {limit: PAGE_SIZE, offset: messages.length})
			.then((conversation) => {
				if (!mounted.current) return
				setMessages((current) => [...current, ...conversation.messages])
				setHasMore(conversation.hasMore)
			})
			.catch(() => {
				if (mounted.current) notification.error("Failed to load earlier messages")
			})
			.finally(() => {
				if (mounted.current) setLoadingMore(false)
			})
	}

	if (loading) {
		return <SMSThreadSkeleton />
	}

	if (!timeline) {
		return null
	}

	const replyTarget = selectedReplyMessageId ?? timeline.replyAnchorProviderMessageId

	return (
		<Box
			sx={{borderTop: `1px solid ${colors.gray["200"]}`, pt: 2.5}}
			display="flex"
			gap={3}
			alignItems="flex-start"
		>
			<Box flex={1} minWidth={0}>
				<SMSThread
					messages={messages}
					replyAnchorProviderMessageId={timeline.replyAnchorProviderMessageId}
					replyTargetProviderMessageId={replyTarget}
					hasMore={hasMore}
					loadingMore={loadingMore}
					onShowEarlier={showEarlier}
					onDraftReply={setSelectedReplyMessageId}
				/>
			</Box>
			{replyTarget && (
				<Box flex={1} minWidth={0}>
					{/* Keyed on the target so picking a different message starts a fresh
					    draft rather than carrying the last one's instructions over. */}
					<SMSDraftReplyPanel
						key={replyTarget}
						fromNumber={timeline.toAddress}
						toNumber={timeline.fromAddress}
					/>
				</Box>
			)}
		</Box>
	)
}

function SMSThreadSkeleton() {
	return (
		<Box display="flex" flexDirection="column" gap={2.5}>
			{[0, 1].map((i) => (
				<Box key={i} p={4} border="1px solid" borderColor="grey.200" borderRadius={1}>
					<Box display="flex" justifyContent="space-between" mb={1.5}>
						<Skeleton variant="text" width={160} />
						<Skeleton variant="rounded" width={72} height={20} />
					</Box>
					<Skeleton variant="text" width="90%" />
				</Box>
			))}
		</Box>
	)
}

export {SMSConversationView}
